// ============================================================
// TrainingTypesCard.js — per-Client Training Type overlay.
// Per-trainingTypeId PATCH; Save loops over dirty rows.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import Switch from '../../components/Switch';
import { toast } from '../../components/Toaster';
import {
  getClientTrainingTypeConfig, patchClientTrainingTypeConfigOne,
} from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

export default function TrainingTypesCard({ clientId }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientTrainingTypeConfig(clientId);
      const list = res.items || [];
      setItems(list);
      setDraft(Object.fromEntries(list.map((r) => [r.trainingTypeId, {
        isEnabled: r.isEnabled,
        customExpiryMonths: r.validityMonthsOverride ?? '',
      }])));
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  function setRowField(id, key, value) {
    setDraft((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  }

  async function save() {
    setSaving(true);
    const dirty = items.filter((r) => {
      const d = draft[r.trainingTypeId];
      const beforeMonths = r.validityMonthsOverride ?? '';
      return d.isEnabled !== r.isEnabled || d.customExpiryMonths !== beforeMonths;
    });
    let succeeded = 0, failed = 0;
    for (const r of dirty) {
      const d = draft[r.trainingTypeId];
      try {
        const months = d.customExpiryMonths === '' ? null : parseInt(d.customExpiryMonths, 10);
        await patchClientTrainingTypeConfigOne(clientId, r.trainingTypeId, {
          isEnabled: d.isEnabled,
          customExpiryMonths: months,
        });
        succeeded++;
      } catch (err) {
        failed++;
        toast.error(`${r.code}: ${translateError(err)}`);
      }
    }
    if (succeeded > 0) toast.success(`Saved ${succeeded} change${succeeded === 1 ? '' : 's'}`);
    if (failed === 0) await fetchData();
    setSaving(false);
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error)   return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  return (
    <div>
      <table className="pc-table">
        <thead>
          <tr>
            <th>Training Type</th>
            <th style={{ width: 130 }}>Default Expiry</th>
            <th style={{ width: 180 }}>Custom Expiry (months)</th>
            <th style={{ width: 80 }}>Enabled</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const d = draft[r.trainingTypeId] || { isEnabled: r.isEnabled, customExpiryMonths: '' };
            return (
              <tr key={r.trainingTypeId}>
                <td>
                  <strong>{r.title}</strong>
                  <div style={{ fontSize: 11, color: '#888' }}>{r.code}</div>
                </td>
                <td style={{ fontSize: 13 }}>
                  {r.hasExpiry ? `${r.defaultValidityMonths || '—'} months` : <span style={{ color: '#888' }}>No expiry</span>}
                </td>
                <td>
                  {r.hasExpiry ? (
                    <input
                      type="number"
                      min={1}
                      max={120}
                      placeholder={String(r.defaultValidityMonths || '')}
                      value={d.customExpiryMonths}
                      onChange={(e) => setRowField(r.trainingTypeId, 'customExpiryMonths', e.target.value)}
                    />
                  ) : <span style={{ color: '#888' }}>—</span>}
                </td>
                <td>
                  <Switch
                    checked={d.isEnabled}
                    onChange={(next) => setRowField(r.trainingTypeId, 'isEnabled', next)}
                    ariaLabel={`${r.code} enabled`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="pc-actions">
        <button type="button" className="pc-btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
